import { RiskIndexSource } from '@app/src/types/resourceExplorer'
import {
  AirSharp,
  BalanceSharp,
  CloudQueueSharp,
  EmojiPeopleOutlined,
  FmdBad,
  ForestSharp,
  QuestionMark,
  Spa,
  Waves,
} from '@mui/icons-material'
import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import React from 'react'
import ItucLogoIcon from './ItucLogoIcon'
import MinderooFoundationLogoIcon from './MinderooFoundationLogoIcon'
import OurWorldInDataPrivateCivilLibertiesLogoIcon from './OurWorldinDataPrivateCivilLibertiesLogoIcon'
import SaveTheChildrenLogoIcon from './SaveTheChildrenLogoIcon'
import { TransparencyInternationalLogoIcon } from './TransparencyInternationalLogoIcon'
import UndpLogoIcon from './UndpLogoIcon'
import WgiLogoIcon from './WgiLogoIcon'
import WorldfavorLogoIcon from './WorldfavorLogoIcon'
import { WriLogoIcon } from './WriLogoIcon'
import WwfAirCondition from './WwfAirCondition'
import WwfLogoIcon from './WwfLogoIcon'

interface RiskIndexIconProps {
  source: RiskIndexSource
  onClick?: () => void
}

export const getIndexIcon = (source: RiskIndexSource) => {
  switch (source) {
    case RiskIndexSource.WriWaterRisk:
      return <WriLogoIcon />
    case RiskIndexSource.WoldBankGroupHumanRights:
      return <WgiLogoIcon />
    case RiskIndexSource.TransparencyInternationalCorruption:
      return <TransparencyInternationalLogoIcon />
    case RiskIndexSource.MinderooFoundationModernSlavery:
      return <MinderooFoundationLogoIcon />
    case RiskIndexSource.ItucLaborRights:
      return <ItucLogoIcon />
    case RiskIndexSource.WwfSoilCondition:
      return <WwfLogoIcon />
    case RiskIndexSource.SaveTheChildrenEndOfChildHood:
      return <SaveTheChildrenLogoIcon />
    case RiskIndexSource.UndpHumanDevelopment:
      return <UndpLogoIcon />
    case RiskIndexSource.WorldfavorDeforestation:
    case RiskIndexSource.WorldfavorHealthAndSafety:
    case RiskIndexSource.WorldfavorWasteCircularResourceUse:
    case RiskIndexSource.WorldfavorChemicalUse:
    case RiskIndexSource.WorldfavorLocalCommunitiesLandUse:
    case RiskIndexSource.WorldfavorPrivacyAndDataSecurity:
    case RiskIndexSource.WorldfavorDiversityEquityInclusion:
    case RiskIndexSource.WorldfavorBiodiversity:
    case RiskIndexSource.WorldfavorPollution:
    case RiskIndexSource.WorldfavorPhysicalClimateRisk:
    case RiskIndexSource.WorldfavorFairAndDecentWages:
      return <WorldfavorLogoIcon />
    case RiskIndexSource.WwfAirCondition:
      return <WwfAirCondition />
    case RiskIndexSource.OurWorldInDataPrivateCivilLiberties:
      return <OurWorldInDataPrivateCivilLibertiesLogoIcon />
    case RiskIndexSource.OurWorldInDataHumanRights:
      return <OurWorldInDataPrivateCivilLibertiesLogoIcon />
    default:
      return <QuestionMark color="primary" />
  }
}

const topicStyling: SxProps<Theme> = ({ palette, spacing }) => ({
  background: palette.secondary.main,
  position: 'absolute',
  left: 0,
  top: 0,
  padding: 0.25,
  borderBottomRightRadius: spacing(0.5),
})

const getIndexTopic = (source: RiskIndexSource) => {
  switch (source) {
    case RiskIndexSource.WorldfavorBiodiversity:
      return <Spa fontSize="small" sx={topicStyling} />
    case RiskIndexSource.WwfSoilCondition:
      return <Waves fontSize="small" sx={topicStyling} />
    case RiskIndexSource.SaveTheChildrenEndOfChildHood:
      return <FmdBad fontSize="small" sx={topicStyling} />
    case RiskIndexSource.WorldfavorDeforestation:
      return <ForestSharp fontSize="small" sx={topicStyling} />
    case RiskIndexSource.WwfAirCondition:
      return <AirSharp fontSize="small" sx={topicStyling} />
    case RiskIndexSource.WoldBankGroupHumanRights:
      return <EmojiPeopleOutlined fontSize="small" sx={topicStyling} />
    case RiskIndexSource.OurWorldInDataHumanRights:
      return <EmojiPeopleOutlined fontSize="small" sx={topicStyling} />
    case RiskIndexSource.WorldfavorFairAndDecentWages:
      return <BalanceSharp fontSize="small" sx={topicStyling} />
    case RiskIndexSource.WorldfavorPollution:
      return <CloudQueueSharp fontSize="small" sx={topicStyling} />
    default:
      return null
  }
}

const RiskIndexIcon: React.FC<RiskIndexIconProps> = ({ source, onClick }: RiskIndexIconProps) => {
  const icon = getIndexIcon(source)
  const topic = getIndexTopic(source)

  return (
    <Box
      flexGrow={1}
      justifyContent="center"
      alignItems="center"
      display="flex"
      onClick={onClick}
      sx={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {topic}
      {icon}
    </Box>
  )
}

export default RiskIndexIcon
